import React, { useEffect, useState } from "react";
import { cssVariables } from "./colors";
import { useTheme } from "./useTheme";
import { ThemeContext, Theme, ThemeContextType } from "./ThemeContextType";
import { getElectronAPI } from "../utils";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

function getSystemTheme(): "light" | "dark" {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "dark";
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "omnara-theme",
}: ThemeProviderProps) {
  // The stored/selected theme preference (light, dark, or system)
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey) as Theme | null;
      if (stored && (stored === "light" || stored === "dark" || stored === "system")) {
        return stored;
      }
    }
    return defaultTheme;
  });

  // Track the actual resolved theme when using "system" mode
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(() => getSystemTheme());

  // The resolved theme is what actually gets applied (always light or dark)
  const resolvedTheme = theme === "system" ? systemTheme : theme;

  // Update theme preference and persist to localStorage
  const setThemeWithPersistence = (newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem(storageKey, newTheme);
  };

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Apply the resolved theme to the DOM
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove existing theme classes
    root.classList.remove("light", "dark");

    // Add current theme class (always light or dark)
    root.classList.add(resolvedTheme);

    // Apply CSS custom properties
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Update theme-color meta tag for PWA browser bar and Electron window chrome.
    // Must match the Electron BrowserWindow backgroundColor and inline splash colors.
    const themeColor = resolvedTheme === "light" ? "#F5F5F5" : "#1A1A1A";
    let metaThemeColor = document.querySelector('meta[name="theme-color"]:not([media])');
    if (!metaThemeColor) {
      // Remove media-specific meta tags and create a single dynamic one
      document.querySelectorAll('meta[name="theme-color"]').forEach((el) => el.remove());
      metaThemeColor = document.createElement("meta");
      metaThemeColor.setAttribute("name", "theme-color");
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute("content", themeColor);
  }, [resolvedTheme]);

  useEffect(() => {
    // Guard: only available in the desktop app (not web).
    getElectronAPI()
      ?.setResolvedTheme?.(theme)
      .catch(() => {});
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme: setThemeWithPersistence,
    toggleTheme: () => setThemeWithPersistence(resolvedTheme === "light" ? "dark" : "light"),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// Theme toggle component
interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className = "" }: ThemeToggleProps) {
  const { resolvedTheme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg bg-muted hover:bg-accent transition-colors ${className}`}
      aria-label="Toggle theme"
    >
      {resolvedTheme === "dark" ? (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ) : (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      )}
    </button>
  );
}
